import { DialogStateManager, useDialog } from '@plato/ui-legacy/components/dialog/dialog-provider';
import { Button } from '@plato/ui-legacy/components/ui/button';
import { Text } from '@plato/ui-legacy/components/ui/text';
import { useToast } from '@plato/ui-legacy/components/ui/use-toast';
import { useResolvedLanguage } from '@plato/ui-legacy/lib/hooks/locale';
import {
    Dialog,
    DialogBody,
    DialogContent,
    DialogFooter,
    DialogHeader,
    DialogIconClose,
    DialogTitle,
} from '@plato/ui/containers/dialog';
import { Waveform } from '@plato/ui/primitives/waveform';
import { cn } from '@plato/ui/util/classes';
import { captureException } from '@sentry/react';
import { useEffect, useId, useRef, useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { useVoiceRecording } from '../../../lib/contexts/voice-recording-context.tsx';
import { useAnalytics } from '../../analytics/hooks/use-analytics.ts';
import type { VoiceInputPayload } from '../../analytics/types/analytics-events.ts';
import type { ModalInnerProps } from '../../modal/index.tsx';
import { useDeepgram } from '../hooks/use-deepgram.ts';
import { useMicrophone } from '../hooks/use-microphone.ts';

export type RecordingDialogProps = {
    trackingContext: VoiceInputPayload;
    onTranscribed: (text: string) => void;
} & ModalInnerProps;

function formatElapsed(ms: number): string {
    const seconds = Math.floor(ms / 1000);
    return `${Math.floor(seconds / 60)}:${(seconds % 60).toString().padStart(2, '0')}`;
}

export const RecordingDialog = DialogStateManager.create<RecordingDialogProps>(
    ({ trackingContext, onTranscribed }): ReactNode => {
        const { visible, remove } = useDialog();
        const sessionId = useId();
        const { t } = useTranslation();
        const language = useResolvedLanguage();
        const { toast } = useToast();
        const { currentSessionId, startSession, endSession } = useVoiceRecording();
        const sendEvent = useAnalytics();

        const isCanceledRef = useRef(false);
        const hasStartedRef = useRef(false);

        const { isTranscribing, transcribe } = useDeepgram({
            onTranscribed: (result) => {
                endSession(sessionId);

                const request = {
                    recording_duration_ms: result.recordingDurationMs,
                    mime_type: result.mimeType,
                    blob_size_bytes: result.blobSizeBytes,
                    language_mode: result.languageMode,
                    ...trackingContext,
                };

                if (result.transcript) {
                    onTranscribed(result.transcript);
                    sendEvent('voice_transcription_completed', {
                        transcript_text: result.transcript,
                        character_count: result.transcript.length,
                        language,
                        deepgram_request_id: result.requestId,
                        audio_duration_s: result.audioDurationS,
                        confidence: result.confidence,
                        low_confidence_word_count: result.lowConfidenceWordCount,
                        detected_languages: result.detectedLanguages,
                        model_version: result.modelVersion,
                        transcription_latency_ms: result.latencyMs,
                        ...request,
                    });
                } else if (result.recordingDurationMs > 3000) {
                    sendEvent('voice_transcription_empty', {
                        deepgram_request_id: result.requestId,
                        audio_duration_s: result.audioDurationS,
                        ...request,
                    });
                }

                remove();
            },
            onFailed: (failure) => {
                endSession(sessionId);
                sendEvent('voice_transcription_failed', {
                    stage: failure.stage,
                    status_code: failure.statusCode,
                    recording_duration_ms: failure.recordingDurationMs,
                    mime_type: failure.mimeType,
                    blob_size_bytes: failure.blobSizeBytes,
                    language_mode: failure.languageMode,
                    ...trackingContext,
                });
                toast({
                    icon: 'warning',
                    title: t('voice__transcription_error_title', { ns: 'common' }),
                    description: t('voice__transcription_error_description', { ns: 'common' }),
                });
                remove();
            },
        });

        const { isRecording, startRecording, stopRecording } = useMicrophone((audioBlob, duration) => {
            // `duration` is measured from the recorder's start, so it excludes the time the
            // permission prompt was open and is comparable with completed / failed events.
            if (isCanceledRef.current) {
                endSession(sessionId);
                sendEvent('voice_recording_cancelled', {
                    recording_duration_ms: Math.round(duration),
                    ...trackingContext,
                });
                remove();
                return;
            }
            transcribe({ blob: audioBlob, recordingDurationMs: Math.round(duration) });
        });

        const startedAtRef = useRef(performance.now());
        const [elapsedMs, setElapsedMs] = useState(0);
        const [timerRunning, setTimerRunning] = useState(true);

        useEffect(() => {
            if (!timerRunning) {
                return;
            }

            const id = setInterval(() => {
                setElapsedMs(performance.now() - startedAtRef.current);
            }, 250);

            return () => {
                clearInterval(id);
            };
        }, [timerRunning]);

        useEffect(() => {
            if (hasStartedRef.current) {
                return;
            }
            hasStartedRef.current = true;

            // Another surface (e.g. the toolbar mic) is already recording — refuse.
            if (currentSessionId) {
                remove();
                return;
            }

            startSession(sessionId);

            void (async () => {
                try {
                    await startRecording();
                    // Only after the mic is live — firing on mount would count
                    // dialog opens (incl. denied mic permission) as recordings.
                    sendEvent('voice_recording_started', trackingContext);
                } catch (error) {
                    captureException(error);
                    endSession(sessionId);
                    toast({
                        icon: 'warning',
                        title: t('voice__mic_error_title', { ns: 'common' }),
                        description: t('voice__mic_error_description', { ns: 'common' }),
                    });
                    remove();
                }
            })();
            // Mount-only: startSession / endSession, translations, etc. are stable
            // enough that a re-run would double-start recording. hasStartedRef guards
            // Strict-Mode's double-invoke.
            // eslint-disable-next-line react-hooks/exhaustive-deps -- see comment
        }, []);

        const cancel = (): void => {
            if (!isRecording || isCanceledRef.current) {
                return;
            }
            isCanceledRef.current = true;
            setTimerRunning(false);
            stopRecording();
        };

        const finish = (): void => {
            if (!isRecording || isCanceledRef.current) {
                return;
            }
            setTimerRunning(false);
            stopRecording();
        };

        const processing = !isRecording || isTranscribing;
        const elapsedLabel = formatElapsed(elapsedMs);

        return (
            <Dialog open={visible} onClose={cancel} disablePointerDismissal>
                <DialogContent className="sm:max-w-md">
                    <DialogHeader>
                        <DialogTitle>{t('voice__recording_title', { ns: 'common' })}</DialogTitle>
                        <div className="flex items-center gap-1.5">
                            <span
                                className={cn(
                                    'h-2 w-2 animate-pulse rounded-full',
                                    processing ? 'bg-neutral-400' : 'bg-blue-500',
                                )}
                            />
                            <Text size="sm" color="neutral-500" className="tabular-nums">
                                {elapsedLabel}
                            </Text>
                        </div>
                        <DialogIconClose label={t('form__close', { ns: 'common' })} />
                    </DialogHeader>
                    <DialogBody>
                        {processing ? (
                            <div className="flex h-16 items-center justify-center">
                                <Text size="sm" color="neutral-500" className="animate-pulse">
                                    {t('voice__processing', { ns: 'common' })}
                                </Text>
                            </div>
                        ) : (
                            <Waveform />
                        )}
                    </DialogBody>
                    <DialogFooter>
                        <Button
                            variant="subtle"
                            disabled={processing}
                            className="bg-neutral-100 hover:bg-neutral-200"
                            onClick={cancel}
                        >
                            {t('form__cancel', { ns: 'common' })}
                        </Button>
                        <Button variant="primary" disabled={processing} onClick={finish}>
                            {t('voice__done', { ns: 'common' })}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        );
    },
);
